import i18n, { type AppLanguageCode, type TKey, translateApp } from '@repo/i18n'
import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'
export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs))
}

export function formatErrorCode(code?: string, language?: AppLanguageCode) {
	const key = `shared:http_errors.${code?.toLowerCase()}` as TKey
	if (!(code && i18n.exists(key))) {
		return translateError('shared:http_errors.default', language)
	}

	return translateError(key, language)
}

export function formatError(error: unknown, language?: AppLanguageCode) {
	if (!(typeof error === 'object' && error !== null && 'code' in error)) {
		return formatErrorCode(undefined, language)
	}

	if (typeof error.code !== 'string') {
		return formatErrorCode(undefined, language)
	}

	return formatErrorCode(error.code, language)
}

export function isInvalidPasswordError(error: unknown) {
	return (
		typeof error === 'object' &&
		error !== null &&
		'code' in error &&
		error.code === 'invalid_password'
	)
}

function translateError(key: TKey, language?: AppLanguageCode) {
	if (language) {
		return translateApp(language, key)
	}

	return i18n.t(key)
}

/**
 * Function that throws an error, it's useful to prevent impossible states in the application.
 * Example: const id = params.id ?? raise('No id provided'), in this case id is a string instead of string | undefined
 * @param error
 */
export const raise = (error: string): never => {
	throw new Error(error)
}

export const debounce = <T extends (...args: Parameters<T>) => ReturnType<T>>(
	callback: T,
	delay: number
): ((...args: Parameters<T>) => void) => {
	let timeout: ReturnType<typeof setTimeout>

	return (...args: Parameters<T>) => {
		clearTimeout(timeout)
		timeout = setTimeout(() => {
			callback(...args)
		}, delay)
	}
}

export function getInitial(value: string) {
	return value.trim().charAt(0).toUpperCase() || '?'
}

export function getInitials(value: string) {
	return value
		.split(' ')
		.map((part) => part[0] ?? '')
		.join('')
		.slice(0, 2)
		.toUpperCase()
}

export function formatBytes(bytes: number) {
	if (bytes < 1024) {
		return `${bytes} B`
	}
	const kb = bytes / 1024
	if (kb < 1024) {
		return `${kb.toFixed(0)} KB`
	}
	const mb = kb / 1024
	if (mb < 1024) {
		return `${mb.toFixed(1)} MB`
	}
	return `${(mb / 1024).toFixed(1)} GB`
}
